// 掲載書類一覧
import React, { useEffect, useState } from 'react';
import axios from '../../../axios';
import { render } from 'react-dom';
import { handleBrowserBack } from '../../../BrowserBack';
import Maintenance from '../../../molecules/Maintenance';
import LoadingModal from '../../../molecules/LoadingModal';
import StaffNavbar from '../../../molecules/StaffNavbar';
import ReactDatePicker from 'react-datepicker';
import {
  organization_type,
  maintenance_type,
  displayed_document_type,
  http_status_code_list,
} from '../../../Const';
import ja from 'date-fns/locale/ja';
import { displayed_document_columns } from '../../../TableColumns';
import MaintenanceTable from '../../../organisms/MaintenanceTable';
import { Button } from 'react-bootstrap';
import ValidMessage from '../../../atoms/ValidMessage';
interface DisplayedDocumentDatas {
  id: number;
  ja_name: string;
  type: string;
  display_name: string;
  file_name: JSX.Element;
  start_date: string;
  end_date: string;
  button: JSX.Element;
}
interface ErrorText {
  jacode?: string;
  target_date?: string;
}

interface PostData {
  jacode: string;
  type: string;
  target_date: Date | null;
}

const DisplayedDocumentList: React.FC = () => {
  //検索パラメータの初期値設定
  const search_params_init = {
    jacode: '',
    type: '',
    target_date: null,
  };

  const [datas, setDatas] = useState<Array<DisplayedDocumentDatas>>([]);
  const [is_loading, setIsLoading] = useState(false); // ローディング画面表示状態
  const [error_text, setErrorText] = useState<ErrorText>();
  const [search_params, setSearchParam] = useState<PostData>(search_params_init);

  // グローバルナビゲーション表示用
  const [role, setRole] = useState('');
  const [has_loan_aproval_system, setHasLoanAprovalSystem] = useState(false);
  // 組織区分コードの取得
  const node = document.getElementById('displayed-document-list');
  const user_organization = node?.dataset.organization_type as string;

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    //テーブルの初期表示を取得（最新世代表示）
    getLatestGeneration();
    // グローバルナビゲーション表示用
    setUserRole();
  }, []);

  /**
   * 検索パラメータに基準日を追加
   * @param value  基準日
   * @return void
   */
  const handleChangeTargetDate = (value: Date): void => {
    setSearchParam(prevState => {
      return { ...prevState, ['target_date']: value };
    });
  };

  /**
   * 検索パラメータにjaコード、掲載書類を追加
   * @param event イベントオブジェクト
   * @return void
   */
  const handleChangeSearch = (
    event: React.FormEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const { name, value } = event.currentTarget;
    setSearchParam(prevState => {
      return { ...prevState, [name]: value };
    });
  };

  /**
   * 検索ボタン押下時
   * @return Promise<void>
   */
  const searchDisplayedDocument = async (): Promise<void> => {
    try {
      setIsLoading(true); //ローディング表示
      setErrorText({});
      const post_data: PostData = {
        jacode: search_params.jacode,
        type: search_params.type,
        target_date: search_params.target_date,
      };
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/search_display_document`,
        post_data
      );
      // レスポンスデータの判定・加工
      convertDatatableData(response.data);
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false); //ローディング非表示
    }
  };

  /**
   * 最新世代表示ボタン押下時
   * @return Promise<void>
   */
  const getLatestGeneration = async (): Promise<void> => {
    try {
      setIsLoading(true); //ローディング表示
      const response = await axios.get(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/get_display_document_latest_generations`
      );
      // レスポンスデータの判定・加工
      convertDatatableData(response.data);
    } catch (error) {
      console.error(error.response.data);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false); //ローディング非表示
    }
  };

  /**
   * 全世代表示ボタン押下時
   * @return Promise<void>
   */
  const getAllGenerations = async (): Promise<void> => {
    try {
      setIsLoading(true); //ローディング表示
      const response = await axios.get(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/get_display_document_get_all_generations`
      );
      // レスポンスデータの判定・加工
      convertDatatableData(response.data);
    } catch (error) {
      console.error(error.response.data);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false); //ローディング非表示
    }
  };

  /**
   * 掲載書類のダウンロード
   *
   * @param id 該当ファイルの一意の値
   * @param file_name ファイル名
   */
  const downloadFile = (id: number, file_name: string) => {
    try {
      setIsLoading(true); //ローディング表示

      //ファイルをダウンロードする。
      const url = `${location.origin}${process.env.MIX_MOCK_URL}/api/download_display_document?id=${id}`;
      const self_download_link = document.createElement('a');
      document.body.appendChild(self_download_link);
      self_download_link.download = `${file_name}`;
      self_download_link.href = url;
      self_download_link.click();
      self_download_link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert('システムエラーが発生しました');
    } finally {
      setIsLoading(false); //ローディング非表示
    }
  };

  /**
   * レスポンスデータの判定と表示するための加工をする
   * @param response
   * @return void
   */
  const convertDatatableData = (response: any): void => {
    setDatas([]);

    // 取得結果の判定
    if (response.result) {
      Object.values(response.list).map((value: any) => {
        setDatas(prev => [
          ...prev,
          {
            id: value.id,
            ja_name: value.ja_name,
            type: value.type,
            display_name: value.display_name,
            file_name: (
              <a
                className="btn-xl btn_link"
                onClick={() => downloadFile(value.id, value.file_name)}
              >
                {value.file_name}
              </a>
            ),
            start_date: value.start_date,
            end_date: value.end_date,
            button: (
              <Button
                variant="outline-success"
                href={`${location.origin}${process.env.MIX_MOCK_URL}/maintenance/displayed_documents_detail?id=${value.id}`}
                className="btn-text-color"
              >
                詳細
              </Button>
            ),
          },
        ]);
      });
    } else {
      if (typeof response.message === 'object') {
        // バリデーションメッセージの表示
        setErrorText({
          jacode: response.message.jacode,
          target_date: response.message.target_date,
        });
      } else {
        console.error(response.message);
        alert(response.message);
      }
    }
  };

  // ロールを設定する
  const setUserRole = () => {
    if (node === null) {
      // nodeが正しく取得できない場合は空文字とする
      setRole('');
      setHasLoanAprovalSystem(false);
      return;
    }

    // ロール名をセット
    setRole(node.dataset.role as string);

    // 融資稟議システム導入済みフラグをセット
    if ((node.dataset.has_loan_aproval_system as string) === '1') {
      setHasLoanAprovalSystem(true);
    } else {
      setHasLoanAprovalSystem(false);
    }
  };

  return (
    <>
      <StaffNavbar user_role={role} has_loan_aproval_system={has_loan_aproval_system} />
      <Maintenance active={maintenance_type.displayed_documents}>
        <div>
          <div className="search_field">
            {user_organization === organization_type.shinren ? (
              <div className="search_field-box">
                <label>JAコード</label>
                <div className="input-area">
                  <input
                    title="JAコード"
                    name="jacode"
                    type="text"
                    className={`form-control ${error_text?.jacode && 'textarea-error'}`}
                    value={search_params.jacode}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeSearch(event)
                    }
                  />
                  <ValidMessage message={error_text?.jacode} />
                </div>
              </div>
            ) : (
              <div className="search_field-box"></div>
            )}
            <div className="search_field-box">
              <label>掲載書類</label>
              <select
                title="掲載書類種別"
                name="type"
                className="form-control"
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  handleChangeSearch(event)
                }
              >
                <option value=""></option>
                {displayed_document_type.map((option, index) => {
                  return (
                    <option key={index} value={option.key}>
                      {option.value}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="search_field-box">
              <label>基準日</label>
              <div className="input-area">
                <ReactDatePicker
                  className={`form-control ${error_text?.target_date && 'textarea-error'}`}
                  selected={search_params.target_date}
                  onChange={handleChangeTargetDate}
                  locale={ja}
                  dateFormat="yyyy-MM-dd"
                />
                <ValidMessage message={error_text?.target_date} />
              </div>
            </div>
            <div className="search_field-box">
              <div className="btn-success btn-mini" onClick={searchDisplayedDocument}>
                検索
              </div>
              <div className="btn-success btn-mini" onClick={getLatestGeneration}>
                最新世代表示
              </div>
              <div className="btn-success btn-mini" onClick={getAllGenerations}>
                全世代表示
              </div>
            </div>
          </div>
          <MaintenanceTable datas={datas} columns={displayed_document_columns} params={{}} />
          <div className="footer-btn">
            <a
              className="btn btn-mini btn-outline-success btn-text-color"
              href={`${location.origin}${process.env.MIX_MOCK_URL}/maintenance/menu`}
            >
              戻る
            </a>
          </div>
        </div>
        <LoadingModal show={is_loading} />
      </Maintenance>
    </>
  );
};
if (document.getElementById('displayed-document-list')) {
  render(<DisplayedDocumentList />, document.getElementById('displayed-document-list'));
}
